import { useEffect, useMemo, useState } from 'react';
import { useUnit } from 'effector-react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { $cardsListStore, $initialAccountsStore, $profileFeatureStore } from '@lib/store';
import { AccountUtils, FormatUtils } from '@shared/utils';
import { type AccountResponse, AccountType } from '@shared/api/rest/accounts';
import type { CardsResponse } from '@shared/api/rest/cards';
import type { UserResponse, UserWithPaymentInfo } from '@shared/api/rest/users';
import { Button, ErrorMessage, Modal, Multiselect, Select } from '@shared/widgets';

interface IForm {
  account?: number;
  cards: number[];
}
const DEFAULT_VALUES: IForm = { cards: [] };

interface IAddCardsProps {
  isOpen: boolean;
  user: UserWithPaymentInfo | UserResponse;
  cardsItems: CardsResponse[];
  onClose: () => void;
  onSubmit: (data: { userIds: string; cardIds: number[] }) => Promise<unknown>;
}

export const AddCardsModal = (props: IAddCardsProps) => {
  const profileFeatureStore = useUnit($profileFeatureStore.store);
  const userAccountsStore = useUnit($initialAccountsStore.store);
  const userCardsStore = useUnit($cardsListStore.store);

  const [accountList, setAccountList] = useState<AccountResponse[]>([]);

  const { control, formState, handleSubmit, watch, reset } = useForm<IForm>({ defaultValues: DEFAULT_VALUES });
  const account = watch('account');

  const { t } = useTranslation();

  useEffect(() => {
    if (profileFeatureStore.data?.accountFlows) {
      const result = AccountUtils.filterAccounts(profileFeatureStore.data.accountFlows, userAccountsStore.data);
      result && setAccountList(result);
    }
  }, [userAccountsStore.data]);

  useEffect(() => {
    if (!account) return;
    $cardsListStore.events.getUserCards({ AccountId: account, Count: 100 });
  }, [account]);

  const closeHandler = () => {
    props.onClose();
    reset(DEFAULT_VALUES);
  };
  const submitHandler = async (data: IForm) => {
    await props.onSubmit({ userIds: props.user.id, cardIds: data.cards });
    closeHandler();
  };

  const accountCardsList = useMemo(
    () => (accountList.length > 0 ? accountList.filter((account) => account.type === AccountType.Card) : []),
    [accountList],
  );
  return (
    <Modal isOpen={props.isOpen} onClose={closeHandler} title={t('modals.addCards.title')}>
      <form className='flex flex-col gap-4' noValidate onSubmit={handleSubmit(submitHandler)}>
        <Controller
          name='account'
          control={control}
          rules={{ required: t('errors.form.required', { ns: 'common' }) }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <div>
              <Select
                isLoading={userAccountsStore.loading}
                disabled={userAccountsStore.loading}
                items={accountCardsList.map((acc) => ({ value: acc.id, label: FormatUtils.toSliceNumber(acc.accountNumber) }))}
                required
                nullValue={{ isEnable: false, text: t('placeholders.chooseAccount') }}
                value={value}
                onChange={onChange}
              />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='cards'
          control={control}
          rules={{ required: t('errors.form.required', { ns: 'common' }) }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <div>
              <Multiselect
                isLoading={userCardsStore.loading}
                disabled={!account || userCardsStore.loading}
                items={userCardsStore.data
                  .filter((c) => !props.cardsItems.map((uc) => uc.cardId).includes(c.cardId))
                  .map((c) => ({ value: c.cardId, label: FormatUtils.toSliceNumber(c.cardNumber, 16) }))}
                nullValue={{ isEnable: false, text: t('placeholders.chooseCards') }}
                required
                value={value}
                onChange={onChange}
              />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Button className='mt-2' fullWidth color='primary' type='submit' disabled={formState.isSubmitting}>
          {t('buttons.add')}
        </Button>
      </form>
    </Modal>
  );
};
